/**
 * Step 2 of the app registration wizard (#1712): the recipe.
 *
 * appId/version are validated live; the wizard shows the id (`<appId>@<version>`)
 * but the server assigns it. Windows 11 is a locked teaser until the licence is
 * clarified (spec §7, D14). Prereqs come from the catalog.
 */

import React from 'react';
import { Alert, Box, Button, Chip, MenuItem, TextField, Typography } from '@mui/material';
import { AppSummary } from '../../services/types';
import { DetectionType, DraftErrors, Provider, RecipeDraft } from '../../utils/appRecipeDraft';

const PROVIDERS: Array<{ value: Provider; label: string }> = [
  { value: 'choco', label: 'Chocolatey' },
  { value: 'msi', label: 'MSI' },
  { value: 'exe', label: 'EXE' },
  { value: 'script', label: 'PowerShell-Script' },
];

const DETECTION_TYPES: Array<{ value: DetectionType; label: string }> = [
  { value: 'file', label: 'Datei vorhanden' },
  { value: 'registry', label: 'Registry-Schlüssel vorhanden' },
  { value: 'msiProductCode', label: 'MSI-Produktcode installiert' },
  { value: 'script', label: 'Script (Exit 0 + Ausgabe)' },
];

interface Props {
  draft: RecipeDraft;
  errors: DraftErrors;
  catalog: AppSummary[];
  serverErrors: string[];
  onChange: (next: RecipeDraft) => void;
}

export default function RecipeForm({ draft, errors, catalog, serverErrors, onChange }: Props) {
  const set = <K extends keyof RecipeDraft>(key: K, value: RecipeDraft[K]) => onChange({ ...draft, [key]: value });
  const touched = (v: string) => v.length > 0;
  const selfPrefix = `${draft.appId}@`;
  const prereqOptions = catalog
    .map((a) => `${a.appId}@${a.latestVersion}`)
    .filter((id) => !id.startsWith(selfPrefix) && !draft.prereqs.includes(id));

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Typography variant="h6">Rezept</Typography>

      <Box display="flex" gap={2}>
        <TextField
          label="App-ID"
          value={draft.appId}
          onChange={(e) => set('appId', e.target.value)}
          error={touched(draft.appId) && !!errors.appId}
          helperText={touched(draft.appId) && errors.appId ? errors.appId : 'z. B. git, revit, 7zip'}
          required
        />
        <TextField
          label="Version"
          value={draft.version}
          onChange={(e) => set('version', e.target.value)}
          error={touched(draft.version) && !!errors.version}
          helperText={touched(draft.version) && errors.version ? errors.version : 'z. B. 2.51.0'}
          required
        />
      </Box>
      {!errors.appId && !errors.version && (
        <Typography variant="body2" color="text.secondary">
          Rezept-ID: <strong>{`${draft.appId}@${draft.version}`}</strong>
        </Typography>
      )}

      <Box display="flex" gap={2}>
        <TextField label="Name" value={draft.name} onChange={(e) => set('name', e.target.value)} required fullWidth />
        <TextField label="Hersteller" value={draft.vendor} onChange={(e) => set('vendor', e.target.value)} required fullWidth />
      </Box>

      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Betriebssystem
        </Typography>
        <Box display="flex" gap={2} alignItems="center">
          <Chip label="Windows Server 2022" color="primary" />
          <Button variant="outlined" disabled sx={{ opacity: 0.6 }}>
            Windows 11 Enterprise
          </Button>
          <Typography variant="caption" color="text.secondary">
            Nach Lizenzklärung (D14)
          </Typography>
        </Box>
      </Box>

      <TextField select label="Installationsart" value={draft.provider} onChange={(e) => set('provider', e.target.value as Provider)}>
        {PROVIDERS.map((p) => (
          <MenuItem key={p.value} value={p.value}>
            {p.label}
          </MenuItem>
        ))}
      </TextField>
      {(draft.provider === 'msi' || draft.provider === 'exe') && (
        <Box display="flex" gap={2}>
          <TextField label="Installer-ID" value={draft.installerId} onChange={(e) => set('installerId', e.target.value)} fullWidth helperText="aus der Installer-Verwaltung" />
          <TextField label="Befehl" value={draft.command} onChange={(e) => set('command', e.target.value)} fullWidth />
          <TextField label="Argumente" value={draft.args} onChange={(e) => set('args', e.target.value)} fullWidth />
        </Box>
      )}
      {draft.provider === 'script' && (
        <TextField label="Script" value={draft.script} onChange={(e) => set('script', e.target.value)} multiline rows={6} fullWidth />
      )}
      <TextField
        label="Timeout (min)"
        type="number"
        value={draft.timeoutMin}
        onChange={(e) => set('timeoutMin', e.target.value)}
        helperText={errors.timeoutMin ?? 'Nach dieser Zeit bricht die Installation ab'}
      />
      {errors.install && <Alert severity="warning">{errors.install}</Alert>}

      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Erkennungsregeln (alle müssen erfüllt sein)
        </Typography>
        {draft.detection.map((rule, i) => (
          <Box key={i} display="flex" gap={2} mb={1}>
            <TextField
              select
              label={`Regel ${i + 1} – Art`}
              value={rule.type}
              onChange={(e) => set('detection', draft.detection.map((r, j) => (j === i ? { ...r, type: e.target.value as DetectionType } : r)))}
            >
              {DETECTION_TYPES.map((t) => (
                <MenuItem key={t.value} value={t.value}>
                  {t.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label={`Regel ${i + 1} – Wert`}
              value={rule.value}
              onChange={(e) => set('detection', draft.detection.map((r, j) => (j === i ? { ...r, value: e.target.value } : r)))}
              fullWidth
            />
            {draft.detection.length > 1 && (
              <Button onClick={() => set('detection', draft.detection.filter((_, j) => j !== i))}>Entfernen</Button>
            )}
          </Box>
        ))}
        <Button onClick={() => set('detection', [...draft.detection, { type: 'file', value: '' }])}>Regel hinzufügen</Button>
      </Box>

      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Voraussetzungen
        </Typography>
        <Box display="flex" gap={1} flexWrap="wrap" mb={1}>
          {draft.prereqs.map((id) => (
            <Chip key={id} label={id} onDelete={() => set('prereqs', draft.prereqs.filter((p) => p !== id))} />
          ))}
        </Box>
        <TextField
          select
          label="Voraussetzung hinzufügen"
          value=""
          onChange={(e) => set('prereqs', [...draft.prereqs, e.target.value])}
          fullWidth
          helperText={prereqOptions.length === 0 ? 'Keine weitere App im Katalog' : 'Wird vor dieser App installiert und mitgeprüft'}
        >
          {prereqOptions.map((id) => (
            <MenuItem key={id} value={id}>
              {id}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      {serverErrors.length > 0 && (
        <Alert severity="error">
          <Typography variant="body2" fontWeight="bold">
            Der Server hat das Rezept abgelehnt:
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2 }}>
            {serverErrors.map((e) => (
              <li key={e}>{e}</li>
            ))}
          </Box>
        </Alert>
      )}
    </Box>
  );
}
